import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Search,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  X,
  Pencil,
  Trash,
  SearchIcon,
} from "lucide-react";
import { Message, toaster, Modal, Panel, Input, Drawer } from "rsuite";
import { getTablaCitas, saveCancelCita } from "../Entities/Services/ConsultasServices";
import moment, { localeData } from "moment/moment";

export const TablaPacientes = ({ idNegocio }) => {
  const idUser = JSON.parse(localStorage.getItem("auth")).idUser;
  const [fechaHoy] = useState(
    JSON.parse(localStorage.getItem("auth")).confApp.fechas,
  );
  // Estados para filtros
  const [busqueda, setBusqueda] = useState("");
  const [fechaDesde, setFechaDesde] = useState(fechaHoy.fechaFmtUsa);
  const [fechaHasta, setFechaHasta] = useState(fechaHoy.fechaFmtUsa);
  const [data, setData] = useState([]);

  // CONSTANTES PARA CANCELACION
  const [fechaCita, setFechaCita] = useState("");
  const [dataCancel, setDataCancel] = useState("");
  const [motivoCancel, setMotivoCancel] = useState("");
  const [espacio, setEspacio] = useState(0);
  const [openCancel, setOpenCancel] = useState(false);
  const handleClose = () => setOpenCancel(false);

  const initialized = useRef(false);

  // Estado para ordenamiento ({ campo: 'paciente', direccion: 'asc' | 'desc' })
  const [sortConfig, setSortConfig] = useState({
    campo: "fechaHora",
    direccion: "asc",
  });

  const toast = (msg, type = "info") => {
    toaster.push(
      <Message showIcon type={type} closable>
        <strong>{type}!</strong>
        &nbsp; {msg}
      </Message>,
      { duration: 5000, placement: "topCenter" },
    );
  };

  // Estado para filtros aplicados (se actualiza al dar clic en el botón Buscar)
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    busqueda: "",
  });

  // Manejar el cambio de ordenamiento
  const handleSort = (campo) => {
    let direccion = "asc";
    if (sortConfig.campo === campo && sortConfig.direccion === "asc") {
      direccion = "desc";
    }
    setSortConfig({ campo, direccion });
  };

  // Estado para paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const elementosPorPagina = 5;

  // Limpiar filtros
  const resetFiltros = () => {
    setBusqueda("");
    setFechaDesde("");
    setFechaHasta("");
  };

  // Filtrado y ordenado memorizado
  const datosFiltradosYOrdenados = useMemo(() => {
    return data
      .filter((item) => {
        // Filtro por texto (Paciente o Médico)
        const termino = filtrosAplicados.busqueda.toLowerCase();
        const coincideTexto =
          item.paciente.toLowerCase().includes(termino) ||
          item.medico.toLowerCase().includes(termino) ||
          item.estado.toLowerCase().includes(termino);

        return coincideTexto;
      })
      .sort((a, b) => {
        if (!sortConfig.campo) return 0;

        let valorA = a[sortConfig.campo];
        let valorB = b[sortConfig.campo];

        if (valorA < valorB) return sortConfig.direccion === "asc" ? -1 : 1;
        if (valorA > valorB) return sortConfig.direccion === "asc" ? 1 : -1;
        return 0;
      });
  }, [filtrosAplicados, sortConfig]);

  // Renderizar indicador de ordenamiento
  const renderSortIcon = (campo) => {
    if (sortConfig.campo !== campo)
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-40" />;
    return sortConfig.direccion === "asc" ? (
      <ArrowUp className="w-4 h-4 ml-1 text-blue-600" />
    ) : (
      <ArrowDown className="w-4 h-4 ml-1 text-blue-600" />
    );
  };

  const cancelarCita = async () => {
    const datos = {
      id: idNegocio,
      espacio: espacio,
      fecha: fechaCita,
      idUser: idUser,
      motivo: motivoCancel,
    };

    if (!motivoCancel) {
      toast("Debes escribir un motivo antes de guardar", "warning");
      return;
    }

    const res = await saveCancelCita(datos);

    if (res.data === -1) {
      toast(
        "No se ha cancelado la cita, porque ya se registro la consulta.",
        "warning",
      );
      return;
    }
    if (res.data === 0) {
      toast("No se ha cancelado la cita, intente nuevamente.", "warning");
      return;
    } else {
      toast("La cita se ha cancelado de forma correcta.", "success");
      setOpenCancel(false);
      localeData();
    }
  };

  const loadCitas = async () => {
    await getTablaCitas(idNegocio, fechaDesde, fechaHasta)
      .then((res) => {
        setFiltrosAplicados({ busqueda });
        setPaginaActual(1); // Reiniciar a la primera página tras buscar
        setData(res);
      })
      .catch((error) => {
        if (error.status === 401) {
          toast("Debe iniciar sesión, antes de continuar...", "error");
          setTimeout(() => {
            window.location = `/`;
          }, 1200);
        } else {
          toast(error.message, "error");
        }
      });
  };

  // Cálculo de paginación
  const totalPaginas = Math.ceil(
    datosFiltradosYOrdenados.length / elementosPorPagina,
  );
  const datosPaginados = useMemo(() => {
    const inicio = (paginaActual - 1) * elementosPorPagina;
    return datosFiltradosYOrdenados.slice(inicio, inicio + elementosPorPagina);
  }, [datosFiltradosYOrdenados, paginaActual]);

  const handleCancel = (props) => {
    setFechaCita(`${props.fecha} ${props.hora}`);
    setDataCancel(
      `${props.nombre} / (${props.espacio}) ${props.fecha} ${props.hora}`,
    );
    setEspacio(props.espacio);
    setOpenCancel(true);
  };

  // Estilos según el estado
  const getBadgeEstado = (estado) => {
    const estilos = {
      Completada: "bg-emerald-100 text-emerald-800 border-emerald-200",
      Pendiente: "bg-amber-100 text-amber-800 border-amber-200",
      Confirmada: "bg-blue-100 text-blue-800 border-blue-200",
      Cancelada: "bg-rose-100 text-rose-800 border-rose-200",
    };
    return estilos[estado] || "bg-slate-100 text-slate-800 border-slate-200";
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      loadCitas();
    }
  }, []);

  useState(() => {}, [data, datosPaginados]);

  return (
    <div className="p-1 bg-slate-50 min-h-screen">
      <div className="p-6 max-w mx-auto bg-white rounded-xl shadow-md border border-gray-100">
        {/* BARRA DE FILTROS */}
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 space-y-3 md:space-y-0 md:flex md:items-center md:justify-between md:gap-4">
          {/* Filtros de Fecha */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-500 font-medium">Desde:</span>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="bg-transparent text-slate-700 text-sm focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-500 font-medium">Hasta:</span>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="bg-transparent text-slate-700 text-sm focus:outline-none"
              />
            </div>

            {(busqueda || fechaDesde || fechaHasta) && (
              <button
                onClick={resetFiltros}
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition-colors"
                title="Limpiar filtros"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Búsqueda por texto */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por paciente o médico..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={() => loadCitas()}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors"
          >
            <SearchIcon className="w-4 h-4" />
            Buscar...
          </button>
        </div>

        {/* TABLA DE DATOS */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100/60 text-slate-600 text-xs uppercase tracking-wider">
                <th
                  onClick={() => handleSort("paciente")}
                  className="py-3 px-4 font-semibold cursor-pointer select-none hover:bg-slate-200/50 transition-colors"
                >
                  <div className="flex items-center">
                    PACIENTE {renderSortIcon("paciente")}
                  </div>
                </th>
								
								<th
                  className="py-3 px-4 font-semibold cursor-pointer select-none hover:bg-slate-200/50 transition-colors"
                >
                  <div className="flex items-center">
                    TELÉFONO 
                  </div>
                </th>

                <th
                  onClick={() => handleSort("fecha")}
                  className="py-3 px-4 font-semibold cursor-pointer select-none hover:bg-slate-200/50 transition-colors"
                >
                  <div className="flex items-center">
                    FECHA {renderSortIcon("fecha")}
                  </div>
                </th>

                <th
                  onClick={() => handleSort("hora")}
                  className="py-3 px-4 font-semibold cursor-pointer select-none hover:bg-slate-200/50 transition-colors"
                >
                  <div className="flex items-center">
                    HORA {renderSortIcon("hora")}
                  </div>
                </th>

                <th
                  onClick={() => handleSort("medico")}
                  className="py-3 px-4 font-semibold cursor-pointer select-none hover:bg-slate-200/50 transition-colors"
                >
                  <div className="flex items-center">
                    MÉDICO {renderSortIcon("medico")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("estado")}
                  className="py-3 px-4 font-semibold cursor-pointer select-none hover:bg-slate-200/50 transition-colors"
                >
                  <div className="flex items-center">
                    ESTADO {renderSortIcon("estado")}
                  </div>
                </th>
                <th className="py-3 px-4 font-semibold text-right">ACCIONES</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 text-sm text-slate-700">
              {datosPaginados.length > 0 ? (
                datosPaginados.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-slate-50/80 transition-colors"
                  >
                    <td className="py-3.5 px-4 font-medium text-slate-900">
                      {item.paciente}
                    </td>
										<td className="py-3.5 px-4">{item.telefono}</td>
                    <td className="py-3.5 px-4">{item.fecha}</td>
                    <td className="py-3.5 px-4">{item.hora}</td>
                    <td className="py-3.5 px-4">{item.medico}</td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getBadgeEstado(item.estado)}`}
                      >
                        {item.estado}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* <button
                          onClick={() =>
                            alert(`Ver detalle de: ${item.paciente}`)
                          }
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Ver detalle"
                        >
                          <Eye className="w-4 h-4" />
                        </button> */}

                        {/* <button
                          onClick={() =>
                            alert(`Editar cita de: ${item.paciente}`)
                          }
                          className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                          title="Editar"
                        >
                          <Pencil className="w-4 h-4" />
                        </button> */}

                        <button
                          onClick={() =>
                            handleCancel({
                              nombre: item.paciente,
                              fecha: item.fecha,
                              espacio: item.espacio,
                              hora: item.hora,
                              estatus: item.estado,
                            })
                          }
                          disabled={
                            item.estado === "Completada" ||
                            item.estado === "Realizada" ||
                            item.estado === "Cancelada"
                              ? true
                              : false
                          }
                          className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                          title="Cancelar"
                        >
                          <Trash className="w-4 h-4" style={{ color: "red" }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-slate-400">
                    No se encontraron registros con los filtros seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPaginas > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              Página {paginaActual} de {totalPaginas}
            </span>
            <div className="flex gap-2">
              <button
                disabled={paginaActual === 1}
                onClick={() => setPaginaActual((prev) => prev - 1)}
                className="px-3 py-1.5 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                disabled={paginaActual === totalPaginas}
                onClick={() => setPaginaActual((prev) => prev + 1)}
                className="px-3 py-1.5 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> MODAL PARA CANCELACIONES <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
      <Modal
        open={openCancel}
        onClose={handleClose}
        backdrop="static"
        overflow="false"
      >
        <Modal.Header>
          <Modal.Title style={{ fontSize: "16px" }}>{dataCancel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Panel bordered>
            <div className="row">
              <div className="col">
                <b>Motivo</b>
                <Input
                  as="textarea"
                  rows={4}
                  placeholder="Escriba el motivo de la cancelación de la cita."
                  onChange={(e) => setMotivoCancel(e)}
                />
              </div>
            </div>
          </Panel>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={handleClose} className="w-50 px-4 py-2 bg-slate-100 text-sm font-semibold text-slate-600 hover:bg-slate-300 rounded-xl transition-colors">
            Cerrar
          </button>
					<button onClick={cancelarCita} type="primary" className="w-50 px-4 py-2 text-xs font-semibold flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl transition-colors shadow-md shadow-cyan-600/20">
            Guardar
          </button>
        </Modal.Footer>
      </Modal>
			
    </div>
  );
};
